import React, { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import { motion } from 'framer-motion'
import {
  DollarSign,
  Users,
  TrendingUp,
  AlertTriangle,
  Settings,
  RefreshCw,
  Receipt,
  PiggyBank,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  XCircle,
  Clock,
  Star,
  BarChart3,
  Target,
  Activity
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'
import { useTheme } from '../../context/ThemeContext'

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })

const RevenueChart = ({ data, selectedMetric }) => {
  const { isDark } = useTheme()
  const [tooltipData, setTooltipData] = useState(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const lastTooltipRef = useRef(null)

  const gridColor = isDark ? '#2f2b41' : '#e5e7eb'
  const axisColor = isDark ? '#2f2b41' : '#d1d5db'
  const tickColor = isDark ? '#a3a3a3' : '#6b7280'

  const handleMouseMove = useCallback((e) => {
    if (e && e.activePayload && e.activePayload.length > 0) {
      const newLabel = e.activeLabel
      const newMousePosition = { x: e.chartX, y: e.chartY }

      if (!lastTooltipRef.current || lastTooltipRef.current !== newLabel) {
        setTooltipData({ label: newLabel, payload: e.activePayload })
        lastTooltipRef.current = newLabel
      }

      setMousePosition(prevPos => {
        if (Math.abs(prevPos.x - newMousePosition.x) > 15 || Math.abs(prevPos.y - newMousePosition.y) > 15) {
          return newMousePosition
        }
        return prevPos
      })
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setTooltipData(null)
    setMousePosition({ x: 0, y: 0 })
    lastTooltipRef.current = null
  }, [])

  return (
    <div style={{ position: 'relative', width: '100%', height: '300px', overflow: 'visible' }}>
      <ResponsiveContainer key={isDark ? 'revenue-chart-dark' : 'revenue-chart-light'} width="100%" height={300}>
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis dataKey="name" tick={{ fill: tickColor, fontSize: 12 }} axisLine={{ stroke: axisColor }} tickLine={{ stroke: axisColor }} />
          <YAxis tick={{ fill: tickColor, fontSize: 12 }} axisLine={{ stroke: axisColor }} tickLine={{ stroke: axisColor }} />
          <Line
            type="monotone"
            dataKey={selectedMetric}
            stroke="#533AFD"
            strokeWidth={3}
            dot={{ fill: '#533AFD', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#533AFD', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>

      {tooltipData && (
        <div
          className="bg-white dark:bg-[#1a1625] border border-neutral-200 dark:border-neutral-600 rounded-lg p-3 shadow-lg"
          style={{
            position: 'absolute',
            left: mousePosition.x + 10,
            top: mousePosition.y - 10,
            zIndex: 1000,
            pointerEvents: 'none',
            fontSize: '14px'
          }}
        >
          <p className="font-medium text-neutral-900 dark:text-neutral-100 mb-1">{tooltipData.label}</p>
          {tooltipData.payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'revenue' ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

const StatCard = React.memo(({ title, value, icon: Icon, change, changeType = 'positive', suffix = '', isLoading = false, index = 0, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
  >
    <Card className="hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">{title}</p>
            {isLoading ? (
              <div className="animate-pulse bg-neutral-300 dark:bg-neutral-600 h-8 w-20 rounded"></div>
            ) : (
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                {typeof value === 'number' ? value.toLocaleString() : value}{suffix}
              </p>
            )}
            {description && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{description}</p>
            )}
          </div>
          <div className="h-12 w-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg">
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
        {change !== undefined && !isLoading && (
          <div className="mt-4 flex items-center space-x-1">
            {changeType === 'positive' ? (
              <ArrowUpRight className="h-4 w-4 text-success-500" />
            ) : (
              <ArrowDownRight className="h-4 w-4 text-error-500" />
            )}
            <span className={cn('text-sm font-medium', changeType === 'positive' ? 'text-success-600' : 'text-error-600')}>
              {Math.abs(change)}%
            </span>
            <span className="text-sm text-neutral-500">vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  </motion.div>
))

const StatusBadge = ({ status }) => {
  const variants = {
    paid: { bg: 'bg-success-100 dark:bg-success-900/20', text: 'text-success-700 dark:text-success-400', icon: CheckCircle2 },
    pending: { bg: 'bg-warning-100 dark:bg-warning-900/20', text: 'text-warning-700 dark:text-warning-400', icon: Clock },
    overdue: { bg: 'bg-error-100 dark:bg-error-900/20', text: 'text-error-700 dark:text-error-400', icon: XCircle },
    cancelled: { bg: 'bg-neutral-100 dark:bg-neutral-800', text: 'text-neutral-700 dark:text-neutral-400', icon: XCircle }
  }
  const variant = variants[status] || variants.pending
  const Icon = variant.icon
  return (
    <span className={cn('inline-flex items-center px-2 py-1 rounded-full text-xs font-medium', variant.bg, variant.text)}>
      <Icon className="h-3 w-3 mr-1" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

const BillingDashboard = () => {
  const [selectedMetric, setSelectedMetric] = useState('revenue')
  const queryClient = useQueryClient()

  // Fetch billing overview
  const { data: billingData, isLoading: billingLoading, error: billingError, refetch: refetchBilling } = useQuery(
    'billing-overview',
    () => apiService.get('/billing/overview').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  // Fetch overages report
  const { data: overagesData, isLoading: overagesLoading, error: overagesError } = useQuery(
    'billing-overages',
    () => apiService.get('/billing/overages').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  // Fetch plans
  const { data: plansData, error: plansError } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data),
    { retry: 1, staleTime: 10 * 60 * 1000 }
  )

  // Fetch historical data for charts
  const { data: historicalData, error: historicalError } = useQuery(
    'billing-historical',
    () => apiService.get('/billing/historical').then(res => res.data),
    { retry: 1, staleTime: 10 * 60 * 1000 }
  )

  // Fetch growth metrics
  const { data: growthData, error: growthError } = useQuery(
    'billing-growth',
    () => apiService.get('/billing/growth').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  // Update billing summary mutation
  const updateSummaryMutation = useMutation(
    () => apiService.post('/billing/update-summary'),
    {
      onSuccess: () => {
        toast.success('Billing summary updated successfully')
        queryClient.invalidateQueries('billing-overview')
        queryClient.invalidateQueries('billing-overages')
        queryClient.invalidateQueries('billing-historical')
        queryClient.invalidateQueries('billing-growth')
      },
      onError: () => {
        toast.error('Failed to update billing summary')
      }
    }
  )


  // Check for errors and provide user feedback
  const hasErrors = billingError || overagesError || plansError || historicalError || growthError
  const isLoading = billingLoading || overagesLoading

  // Use real data or fallbacks
  const chartData = historicalData || [
    { name: 'Jan', revenue: 0, clients: 0, overages: 0 },
    { name: 'Feb', revenue: 0, clients: 0, overages: 0 },
    { name: 'Mar', revenue: 0, clients: 0, overages: 0 },
    { name: 'Apr', revenue: 0, clients: 0, overages: 0 },
    { name: 'May', revenue: 0, clients: 0, overages: 0 },
    { name: 'Jun', revenue: 0, clients: 0, overages: 0 }
  ]

  const growthMetrics = growthData || {
    totalRevenue: { current: 0, previous: 0, growth: 0 },
    baseRevenue: { current: 0, previous: 0, growth: 0 },
    overageRevenue: { current: 0, previous: 0, growth: 0 },
    activeSubscriptions: { current: 0, previous: 0, growth: 0 },
    avgRevenuePerClient: { current: 0, previous: 0, growth: 0 },
    churnRate: { current: 2.5, previous: 2.5, growth: 0 }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading billing data..." />
      </div>
    )
  }

  const displayBillingData = billingData || {
    totalRevenue: 0,
    baseRevenue: 0, 
    overageRevenue: 0,
    activeSubscriptions: 0,
    planBreakdown: [],
    clientBilling: [],
    lastUpdated: new Date().toISOString()
  }
  
  const displayOveragesData = overagesData || {
    totalOverageRevenue: 0,
    overageCount: 0,
    overages: []
  }
  const totalRevenue = displayBillingData.totalRevenue || 0
  const baseRevenue = displayBillingData.baseRevenue || 0
  const overageRevenue = displayBillingData.overageRevenue || 0
  const activeSubscriptions = displayBillingData.activeSubscriptions || 0
  const planBreakdown = displayBillingData.planBreakdown || []
  const clientBilling = displayBillingData.clientBilling || []

  return (
    <div className="space-y-8 pt-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
            Billing Dashboard
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Monitor revenue, subscriptions, and usage billing
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => updateSummaryMutation.mutate()}
            disabled={updateSummaryMutation.isLoading}
            className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41] hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            {updateSummaryMutation.isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Settings className="h-4 w-4 mr-2" />
                Update
              </>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Error Banner */}
      {hasErrors && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-5 w-5 text-warning-600 dark:text-warning-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-warning-800 dark:text-warning-200">
                    Unable to load live billing data
                  </p>
                  <p className="text-xs text-warning-700 dark:text-warning-300 mt-1">
                    Showing demo data. Check your connection or contact support if this persists.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    refetchBilling()
                    queryClient.invalidateQueries('billing-overages')
                    queryClient.invalidateQueries('billing-plans')
                    queryClient.invalidateQueries('billing-historical')
                    queryClient.invalidateQueries('billing-growth')
                  }}
                  className="border-warning-300 dark:border-warning-700 text-warning-700 dark:text-warning-300 hover:bg-warning-100 dark:hover:bg-warning-900/30"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Revenue Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Monthly Revenue"
          value={formatCurrency(totalRevenue)}
          icon={DollarSign}
          change={growthMetrics.totalRevenue.growth}
          changeType={growthMetrics.totalRevenue.growth >= 0 ? "positive" : "negative"}
          index={0}
        />
        <StatCard
          title="Active Subscriptions"
          value={activeSubscriptions}
          icon={Users}
          change={growthMetrics.activeSubscriptions.growth}
          changeType={growthMetrics.activeSubscriptions.growth >= 0 ? "positive" : "negative"}
          index={1}
        />
        <StatCard
          title="Overage Revenue"
          value={formatCurrency(overageRevenue)}
          icon={TrendingUp}
          change={growthMetrics.overageRevenue.growth}
          changeType={growthMetrics.overageRevenue.growth >= 0 ? "positive" : "negative"}
          index={2}
        />
        <StatCard
          title="Avg Revenue per Client"
          value={formatCurrency(growthMetrics.avgRevenuePerClient.current)}
          icon={Target}
          change={growthMetrics.avgRevenuePerClient.growth}
          changeType={growthMetrics.avgRevenuePerClient.growth >= 0 ? "positive" : "negative"}
          index={3}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Revenue Trend Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Card className="border-0 bg-gradient-to-br from-surface-50 to-surface-100 dark:from-dark-200 dark:to-dark-300">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                  <CardTitle className="text-lg font-semibold">Revenue Trend</CardTitle>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    className="text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg pl-2 pr-7 py-1 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-600 cursor-pointer"
                  >
                    <option value="revenue">Revenue</option>
                    <option value="clients">Clients</option>
                    <option value="overages">Overages</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <RevenueChart
                data={chartData}
                selectedMetric={selectedMetric}
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Plan Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <Card className="border-0 bg-gradient-to-br from-surface-50 to-surface-100 dark:from-dark-200 dark:to-dark-300">
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-2">
                <PiggyBank className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                <CardTitle className="text-lg font-semibold">Plan Distribution</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {planBreakdown.length > 0 ? (
                <div className="space-y-4">
                  {planBreakdown.map((plan, index) => {
                    const planColors = {
                      basic: '#10b981',
                      pro: '#533AFD',
                      enterprise: '#f59e0b'
                    }
                    const customColor = '#6c63ff'
                    
                    // Use real plan data from API
                    const getPlanData = (planId) => {
                      if (plansData && plansData[planId]) {
                        return {
                          name: plansData[planId].name,
                          price: plansData[planId].price
                        }
                      }
                      // Fallback to defaults if API data not available
                      const defaults = {
                        basic: { name: 'Basic Plan', price: 29 },
                        pro: { name: 'Pro Plan', price: 79 },
                        enterprise: { name: 'Enterprise Plan', price: 199 }
                      }
                      return defaults[planId] || { name: 'Unknown Plan', price: 0 }
                    }
                    const percentage = activeSubscriptions > 0 ? (plan.count / activeSubscriptions * 100) : 0
                    
                    return (
                      <motion.div 
                        key={plan.plan_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 * index }}
                        className="flex items-center justify-between p-4 bg-white dark:bg-dark-300 rounded-lg shadow-sm"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: (planColors[plan.plan_id] || customColor) + '20' }}>
                            <Star className="h-5 w-5" style={{ color: planColors[plan.plan_id] || customColor }} />
                          </div>
                          <div>
                            <p className="font-medium text-neutral-900 dark:text-neutral-100">
                              {getPlanData(plan.plan_id).name}
                            </p>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                              {formatCurrency(getPlanData(plan.plan_id).price)}/month
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                            {plan.count}
                          </p>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400">
                            {percentage.toFixed(1)}%
                          </p>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <PiggyBank className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                  <p className="text-neutral-600 dark:text-neutral-400">No active subscriptions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Usage Overages */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9 }}
      >
        <Card className="border-0 bg-gradient-to-br from-surface-50 to-surface-100 dark:from-dark-200 dark:to-dark-300">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                <CardTitle className="text-lg font-semibold">Usage Overages</CardTitle>
              </div>
              {overagesData?.overageCount > 0 && (
                <span className="flex items-center text-sm text-warning-600 dark:text-warning-400 bg-warning-100 dark:bg-warning-900/20 px-2 py-1 rounded-full">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  {overagesData.overageCount} clients
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {overagesLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="sm" />
              </div>
            ) : overagesData?.overages?.length > 0 ? (
              <div className="space-y-3">
                {overagesData.overages.slice(0, 5).map((overage, index) => (
                  <motion.div 
                    key={overage.clientId}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 * index }}
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-warning-50 to-warning-100 dark:from-warning-900/10 dark:to-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-warning-500 rounded-lg flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-neutral-100">
                          {overage.companyName}
                        </p>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {overage.used} / {overage.quota} edits (+{overage.overage} over)
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-warning-600 dark:text-warning-400">
                        {formatCurrency(overage.overageCost)}
                      </p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        overage fee
                      </p>
                    </div>
                  </motion.div>
                ))}
                
                {overagesData.overages.length > 5 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.6 }}
                    className="text-center py-2"
                  >
                    <Button variant="outline" size="sm">
                      View {overagesData.overages.length - 5} more
                    </Button>
                  </motion.div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-success-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                  No Usage Overages
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  All clients are within their plan limits this month
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Client Billing Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
      >
        <Card className="border-0 bg-gradient-to-br from-surface-50 to-surface-100 dark:from-dark-200 dark:to-dark-300">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                <CardTitle className="text-lg font-semibold">Client Billing Details</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">Client</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">Plan</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">Plan Cost</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">Overages</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {clientBilling.length > 0 ? (
                    clientBilling.map((client, index) => (
                      <motion.tr 
                        key={client.clientId}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 * index }}
                        className="border-b border-neutral-100 dark:border-neutral-700 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-8 w-8 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center">
                              <Users className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-neutral-900 dark:text-neutral-100">
                                {client.companyName}
                              </p>
                              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                ID: {client.clientId.slice(0, 8)}...
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={cn(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            client.plan === 'basic' ? 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-400'
                            : client.plan === 'pro' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400'
                            : client.plan === 'enterprise' ? 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-400'
                            : 'bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-400'
                          )}>
                            {client.planName || plansData?.[client.plan]?.name || client.plan}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <StatusBadge status={client.status || 'paid'} />
                        </td>
                        <td className="py-4 px-4">
                          <div className="font-medium text-neutral-900 dark:text-neutral-100">
                            {formatCurrency(client.planCost)}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {client.overage && client.overage.hasOverage ? (
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-warning-600 dark:text-warning-400">
                                +{client.overage.overage} edits
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                {formatCurrency(client.overage.overageCost)}
                              </p>
                            </div>
                          ) : (
                            <span className="text-sm text-neutral-400">No overage</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <div className="font-bold text-lg text-neutral-900 dark:text-neutral-100">
                            {formatCurrency(client.totalCost)}
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-12">
                        <div className="space-y-4">
                          <Receipt className="h-12 w-12 text-neutral-400 mx-auto" />
                          <div>
                            <p className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                              No Billing Data
                            </p>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                              No client billing information is available for this period
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Last Updated */}
      {billingData?.lastUpdated && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.1 }}
          className="text-center"
        >
          <div className="inline-flex items-center space-x-2 text-sm text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-[#2f2b41] px-3 py-2 rounded-full">
            <Clock className="h-4 w-4" />
            <span>Last updated: {formatDate(billingData.lastUpdated)}</span>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default BillingDashboard